import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Film, Loader2, Wand2, ImageIcon, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import api from '@/lib/api';
import { mediaUrlForPlayback } from '@/lib/mediaUrls';
import {
  SCRIPT_BASED_PHASE_LABELS,
  buildScriptBasedNextSteps,
  scriptBasedAlignJobStuck,
  scriptBasedImagesInProgress,
  scriptBasedImagesJobStuck,
  scriptBasedImageStallError,
  scriptBasedMissingAlignSegments,
  scriptBasedMissingAudioSegments,
  scriptBasedMissingImageSegments,
  scriptBasedPromptsJobStuck,
  scriptBasedReadyForScenes,
  scriptBasedShouldShowAlignResume,
  scriptBasedShouldShowAudioResume,
  scriptBasedShouldShowImageResume,
  scriptBasedShowSceneViews,
  scriptBasedTtsJobStuck,
  segmentHasAlignTiming,
  segmentHasWhisperxAlign,
} from '@/lib/scriptBasedPipeline';
import { ScriptBasedPipelineSceneViews } from '@/components/ScriptBasedPipelineSceneViews';
import { WebScraperPipelinePanel } from '@/components/WebScraperPipelinePanel';
import {
  isWebScraperPipelineMessage,
  webScraperActiveFromGenerationJob,
} from '@/lib/webScraperPipeline';

export function ScriptBasedPipelineDialog({
  open,
  onOpenChange,
  jobId,
  projectId,
  showCompletionToasts = true,
  onJobChange,
  onJobIdChange,
}) {
  const navigate = useNavigate();
  const [phaseLoading, setPhaseLoading] = useState(null);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [job, setJob] = useState(null);
  const [project, setProject] = useState(null);
  const [imageRetryBusy, setImageRetryBusy] = useState(null);
  const [stallTick, setStallTick] = useState(0);
  const pollRef = useRef(null);

  const updateJob = useCallback((next) => {
    setJob(next);
    onJobChange?.(next);
  }, [onJobChange]);

  const loadProject = useCallback(async (id) => {
    if (!id) return null;
    const { data } = await api.get(`/narration/projects/${id}`);
    setProject(data);
    return data;
  }, []);

  const pollJob = useCallback(async (nextJobId, nextProjectId) => {
    try {
      const { data } = await api.get(`/jobs/${nextJobId}`);
      updateJob(data);
      let loadedProject = null;
      if (nextProjectId) {
        loadedProject = await loadProject(nextProjectId);
      }
      if (data.status === 'completed') {
        clearInterval(pollRef.current);
        if (showCompletionToasts) {
          const segs = loadedProject?.segments || [];
          const missing = segs.filter((s) => s.audio_url && !segmentHasAlignTiming(s));
          const allImagesDone = segs.length > 0 && segs.every((s) => !s.audio_url || (s.image_url || s.source_image_url));
          if (allImagesDone) {
            toast.success('All scene images are ready');
          } else if (missing.length) {
            toast.warning(
              `Pipeline finished but ${missing.length} scene(s) still need WhisperX — use Resume alignment.`,
            );
          } else {
            toast.success('Pipeline step complete — continue with next step');
          }
        }
      } else if (data.status === 'failed') {
        clearInterval(pollRef.current);
        if (nextProjectId) {
          await loadProject(nextProjectId);
        }
        if (showCompletionToasts) {
          toast.error(data.error_message || 'Pipeline failed');
        }
      }
    } catch {
      if (showCompletionToasts) {
        toast.error('Could not check job status');
      }
    }
  }, [loadProject, showCompletionToasts, updateJob]);

  useEffect(() => {
    if (!open || !jobId) {
      return undefined;
    }

    let cancelled = false;
    setJob(null);
    setProject(null);

    const start = async () => {
      try {
        const { data } = await api.get(`/jobs/${jobId}`, { timeout: 20_000 });
        if (cancelled) return;
        updateJob(data);
        if (projectId) {
          await loadProject(projectId);
        }
        if (!['completed', 'failed', 'cancelled'].includes(data.status)) {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = setInterval(() => pollJob(jobId, projectId), 1500);
        } else if (projectId) {
          await loadProject(projectId);
        }
      } catch {
        if (!cancelled) {
          updateJob({
            id: jobId,
            status: 'failed',
            error_message: 'Could not load pipeline status — the server may be busy. Close and try again.',
          });
          if (showCompletionToasts) {
            toast.error('Could not load pipeline status');
          }
        }
      }
    };

    start();

    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [open, jobId, projectId, pollJob, loadProject, updateJob, showCompletionToasts]);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    if (!open || !jobId) return undefined;
    const id = setInterval(() => setStallTick((t) => t + 1), 10000);
    return () => clearInterval(id);
  }, [open, jobId]);

  const isRunning = job && !['completed', 'failed', 'cancelled'].includes(job.status);
  const isComplete = job?.status === 'completed';
  const isFailed = job?.status === 'failed';
  const isCancelled = job?.status === 'cancelled';
  const isStuckQueued = job?.status === 'pending' && (job?.steps_completed || 0) === 0 && projectId;
  const trace = Array.isArray(job?.pipeline_trace) ? job.pipeline_trace : [];
  const segments = project?.segments || [];
  const missingAlignSegments = scriptBasedMissingAlignSegments(segments);
  const missingAudioSegments = scriptBasedMissingAudioSegments(segments);
  const failedAlignSegments = segments.filter((s) => s.audio_url && s.align_status === 'failed');
  const alignedCount = segments.filter((s) => segmentHasAlignTiming(s)).length;
  const alignStuckTimeoutSec = Number(job?.script_based_align_stuck_timeout_sec)
    || Number(job?.script_based_stuck_timeout_sec)
    || 300;
  const showAlignResume = Boolean(projectId) && scriptBasedShouldShowAlignResume(job, segments);
  const isAlignStuck = scriptBasedAlignJobStuck(job, segments);
  const showAudioResume = Boolean(projectId) && scriptBasedShouldShowAudioResume(job, segments);
  const isTtsStuck = scriptBasedTtsJobStuck(job, segments);
  const ttsStuckTimeoutSec = Number(job?.script_based_tts_stuck_timeout_sec)
    || Number(job?.script_based_stuck_timeout_sec)
    || 300;
  const isStuckAlign =
    isRunning
    && projectId
    && typeof job?.message === 'string'
    && job.message.includes('WhisperX alignment')
    && /WhisperX alignment 0\//.test(job.message);
  const resumeBusy = Boolean(resumeLoading);

  const visualStyle = project?.input?.visual_style || '';
  const nextSteps = buildScriptBasedNextSteps(segments, visualStyle);
  const readyForScenes = scriptBasedReadyForScenes(segments, visualStyle);
  const currentStep = nextSteps.find((s) => s.current);
  const showSceneViews = scriptBasedShowSceneViews(segments);
  const imagesInProgress = scriptBasedImagesInProgress(segments, job);
  const missingImageSegments = scriptBasedMissingImageSegments(segments);
  const imageStuckTimeoutSec = Number(job?.script_based_image_stuck_timeout_sec)
    || Number(job?.script_based_stuck_timeout_sec)
    || 300;
  void stallTick;
  const isImageStuck = scriptBasedImagesJobStuck(job, segments);
  const promptsJobStuck = scriptBasedPromptsJobStuck(job, segments);
  const imageStallError = scriptBasedImageStallError(job);
  const showImageResume = Boolean(projectId) && scriptBasedShouldShowImageResume(job, segments);
  const imagePhaseStalled = isImageStuck || imageStallError;
  const isWebScraperProject = project?.input?.visual_style === 'web_scraper';
  const webScraperSnap = webScraperActiveFromGenerationJob(job);
  const showWebScraperPipeline = isWebScraperProject && (
    webScraperSnap
    || isWebScraperPipelineMessage(job?.message)
    || (imagesInProgress && isRunning)
  );
  const stepCompleted = Math.min(job?.steps_completed || 0, job?.total_steps || job?.steps_completed || 0);
  const stepTotal = job?.total_steps || '—';

  const startPolling = useCallback((id) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => pollJob(id, projectId), 1500);
  }, [pollJob, projectId]);

  useEffect(() => {
    if (!open || !projectId || !imagesInProgress) {
      return undefined;
    }
    const refresh = setInterval(() => {
      loadProject(projectId);
    }, 1500);
    return () => clearInterval(refresh);
  }, [open, projectId, imagesInProgress, loadProject]);

  useEffect(() => {
    if (!open || !projectId || !showAlignResume) {
      return undefined;
    }
    const refresh = setInterval(() => {
      loadProject(projectId);
    }, 3000);
    return () => clearInterval(refresh);
  }, [open, projectId, showAlignResume, loadProject]);

  const runPhase = async (phase, request) => {
    if (!projectId) return;
    setPhaseLoading(phase);
    try {
      const { data } = await request();
      const nextJobId = data.job_id || jobId;
      onJobIdChange?.(nextJobId);
      updateJob({
        id: nextJobId,
        status: 'running',
        message: phase === 'prompts' ? 'Generating image prompts' : 'Generating images',
      });
      startPolling(nextJobId);
      await pollJob(nextJobId, projectId);
      toast.message(phase === 'prompts' ? 'Generating image prompts…' : 'Generating images…');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start next step');
    } finally {
      setPhaseLoading(null);
    }
  };

  const generateImagePrompts = () => runPhase(
    'prompts',
    () => api.post(`/narration/script-based/projects/${projectId}/generate-image-prompts`),
  );

  const generateImages = () => runPhase(
    'images',
    () => api.post(`/narration/script-based/projects/${projectId}/generate-images`),
  );

  const retryPipeline = async () => {
    if (!projectId) return;
    try {
      const { data } = await api.post(`/narration/script-based/projects/${projectId}/retry`);
      const nextJobId = data.job_id;
      onJobIdChange?.(nextJobId);
      updateJob({ id: nextJobId, status: 'pending', progress: 0, message: 'Retrying…', pipeline_trace: [] });
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(() => pollJob(nextJobId, projectId), 2000);
      await pollJob(nextJobId, projectId);
      toast.message('Pipeline retry started');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not retry pipeline');
    }
  };

  const resumeAlign = async () => {
    if (!projectId) return;
    setResumeLoading(true);
    try {
      const { data } = await api.post(`/narration/script-based/projects/${projectId}/resume-align`);
      const nextJobId = data.job_id || jobId;
      onJobIdChange?.(nextJobId);
      updateJob({
        id: nextJobId,
        status: 'running',
        message: 'Resuming WhisperX alignment',
        error_message: null,
      });
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(() => pollJob(nextJobId, projectId), 2000);
      await pollJob(nextJobId, projectId);
      toast.message(
        missingAlignSegments.length
          ? `Aligning ${missingAlignSegments.length} remaining scene(s)…`
          : 'WhisperX alignment resumed',
      );
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 409) {
        toast.message(detail || 'WhisperX resume already in progress');
      } else {
        toast.error(detail || 'Could not resume alignment');
      }
    } finally {
      setResumeLoading(false);
    }
  };

  const resumeImages = async () => {
    if (!projectId) return;
    setResumeLoading(true);
    try {
      const { data } = await api.post(`/narration/script-based/projects/${projectId}/resume-images`);
      const nextJobId = data.job_id || jobId;
      onJobIdChange?.(nextJobId);
      updateJob({
        id: nextJobId,
        status: 'running',
        message: 'Resuming image generation',
        error_message: null,
      });
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(() => pollJob(nextJobId, projectId), 2000);
      await pollJob(nextJobId, projectId);
      toast.message(
        missingImageSegments.length
          ? `Generating images for ${missingImageSegments.length} remaining scene(s)…`
          : 'Image generation resumed',
      );
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 409) {
        toast.message(detail || 'Image generation resume already in progress');
      } else {
        toast.error(detail || 'Could not resume image generation');
      }
    } finally {
      setResumeLoading(false);
    }
  };

  const skipFailedAlign = async () => {
    if (!projectId) return;
    setResumeLoading(true);
    try {
      const { data } = await api.post(`/narration/script-based/projects/${projectId}/skip-failed-align`);
      const nextJobId = data.job_id || jobId;
      onJobIdChange?.(nextJobId);
      updateJob({
        id: nextJobId,
        status: 'running',
        message: 'Continuing without WhisperX for failed scenes',
        error_message: null,
      });
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(() => pollJob(nextJobId, projectId), 2000);
      await pollJob(nextJobId, projectId);
      toast.message('Using audio duration for failed scenes — continuing pipeline…');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not continue pipeline');
    } finally {
      setResumeLoading(false);
    }
  };

  const regenerateSegmentImage = useCallback(async (scene) => {
    const panelNumber = Number(scene?.segment_number || scene?.panel_number);
    const segmentId = scene?.id
      || (segments || []).find((s) => Number(s.segment_number) === panelNumber)?.id;
    if (!projectId || !segmentId) {
      toast.error('Cannot regenerate — missing project or scene');
      return;
    }
    setImageRetryBusy(panelNumber);
    try {
      const { data } = await api.post(
        `/narration/projects/${projectId}/segments/${segmentId}/regenerate-image`,
        { async_job: true },
      );
      if (data?.status === 'retry_scheduled') {
        toast.message(data.message || 'Image queued for automatic retry');
      } else if (data?.job_id) {
        await new Promise((resolve, reject) => {
          const started = Date.now();
          const interval = setInterval(async () => {
            try {
              const { data: job } = await api.get(`/jobs/${data.job_id}`);
              if (job.status === 'completed' || job.status === 'retry_scheduled') {
                clearInterval(interval);
                resolve(null);
              } else if (job.status === 'failed') {
                clearInterval(interval);
                reject(new Error(job.error_message || 'Image regeneration failed'));
              } else if (Date.now() - started > 10 * 60 * 1000) {
                clearInterval(interval);
                reject(new Error('Image regeneration timed out'));
              }
            } catch (pollErr) {
              if (Date.now() - started > 10 * 60 * 1000) {
                clearInterval(interval);
                reject(pollErr);
              }
            }
          }, 2500);
        });
        toast.success(`Scene ${panelNumber} image regenerated`);
      } else {
        toast.success(`Scene ${panelNumber} image regenerated`);
      }
      await loadProject(projectId);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Image regeneration failed');
    } finally {
      setImageRetryBusy(null);
    }
  }, [projectId, loadProject, segments]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${showSceneViews ? 'max-w-5xl' : 'max-w-2xl'} max-h-[90vh] overflow-hidden flex flex-col`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isComplete ? (
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            ) : isFailed || imagePhaseStalled ? (
              <AlertCircle className="w-5 h-5 text-red-600" />
            ) : isRunning ? (
              <Loader2 className="w-5 h-5 animate-spin text-violet-600" />
            ) : (
              <Loader2 className="w-5 h-5 text-slate-400" />
            )}
            Script-based pipeline
          </DialogTitle>
        </DialogHeader>
        {job ? (
          <div className="space-y-4 overflow-y-auto flex-1 pr-1">
            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">{job.message || job.status}</p>
              <Progress value={job.progress || 0} className="h-2" />
              <p className="text-xs text-slate-500 mt-1">
                Step {stepCompleted} / {stepTotal}
              </p>
            </div>
            {showWebScraperPipeline ? (
              <WebScraperPipelinePanel
                projectId={projectId}
                jobId={webScraperSnap?.jobId || job?.asset_search_job_id}
                snapshot={webScraperSnap ? {
                  ...webScraperSnap,
                  message: job?.message,
                } : null}
              />
            ) : null}
              {job.error_message && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-3 space-y-2">
                  <p className="text-sm text-red-700">{job.error_message}</p>
                  {showAlignResume && missingAlignSegments.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={resumeBusy || Boolean(phaseLoading)}
                        onClick={resumeAlign}
                      >
                        {resumeBusy ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : null}
                        Resume WhisperX alignment
                      </Button>
                      {failedAlignSegments.length > 0 && (
                        <Button
                          type="button"
                          size="sm"
                          disabled={resumeBusy || Boolean(phaseLoading)}
                          onClick={skipFailedAlign}
                        >
                          Continue without WhisperX for failed scenes
                        </Button>
                      )}
                    </div>
                  )}
                  {(showImageResume || (imageStallError && projectId)) && (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={resumeBusy || Boolean(phaseLoading)}
                        onClick={resumeImages}
                      >
                        {resumeBusy ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : null}
                        Resume image generation
                      </Button>
                    </div>
                  )}
                </div>
              )}
              {isCancelled && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                  <p className="mb-1 font-medium">This pipeline run was cancelled.</p>
                  <p>
                    The step log below is only for this job
                    {alignedCount > 0
                      ? ` · ${alignedCount}/${segments.length} scenes already have WhisperX timing on the project`
                      : ''}
                    . Use the next steps below to continue on the project.
                  </p>
                </div>
              )}

              {segments.length > 0 && (
                <div className="rounded-lg border border-violet-100 bg-violet-50/50 p-3 space-y-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-violet-700">Next steps</h3>
                  <ul className="space-y-2">
                    {nextSteps.map((step) => (
                      <li
                        key={step.id}
                        className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border px-3 py-2 text-sm ${
                          step.current
                            ? 'border-violet-300 bg-white shadow-sm'
                            : step.done
                              ? 'border-emerald-200 bg-emerald-50/60'
                              : 'border-slate-200 bg-white/60 opacity-80'
                        }`}
                      >
                        <div className="flex items-start gap-2 min-w-0">
                          {step.done ? (
                            <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                          ) : step.current ? (
                            <Loader2 className={`w-4 h-4 text-violet-600 shrink-0 mt-0.5 ${(isRunning && !(step.id === 'prompts' && promptsJobStuck)) || phaseLoading ? 'animate-spin' : ''}`} />
                          ) : (
                            <span className="w-4 h-4 rounded-full border border-slate-300 shrink-0 mt-0.5" />
                          )}
                          <div className="min-w-0">
                            <p className="font-medium text-slate-800">{step.label}</p>
                            <p className="text-xs text-slate-500">{step.detail}</p>
                          </div>
                        </div>
                        {step.id === 'align' && step.current && !step.done && (
                          showAlignResume ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={resumeBusy || Boolean(phaseLoading)}
                              onClick={resumeAlign}
                            >
                              {resumeBusy ? (
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              ) : null}
                              Continue WhisperX
                            </Button>
                          ) : (
                            isRunning && missingAlignSegments.length > 0 && (
                              <Button type="button" size="sm" disabled>
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                Aligning…
                              </Button>
                            )
                          )
                        )}
                        {step.id === 'prompts' && step.current && !step.done && !isWebScraperProject && (
                          <Button
                            type="button"
                            size="sm"
                            disabled={Boolean(phaseLoading) || (isRunning && !promptsJobStuck)}
                            onClick={generateImagePrompts}
                          >
                            {phaseLoading === 'prompts' ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <Wand2 className="w-4 h-4 mr-1" />
                            )}
                            Generate image prompts
                          </Button>
                        )}
                        {step.id === 'images' && step.current && !step.done && (
                          showImageResume ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={resumeBusy || Boolean(phaseLoading)}
                              onClick={resumeImages}
                            >
                              {resumeBusy ? (
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              ) : (
                                <ImageIcon className="w-4 h-4 mr-1" />
                              )}
                              Resume image generation
                            </Button>
                          ) : (
                            !imagePhaseStalled && (
                            <Button
                              type="button"
                              size="sm"
                              disabled={Boolean(phaseLoading) || isRunning || imagesInProgress}
                              onClick={generateImages}
                            >
                              {phaseLoading === 'images' || imagesInProgress ? (
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              ) : (
                                <ImageIcon className="w-4 h-4 mr-1" />
                              )}
                              {phaseLoading === 'images' || imagesInProgress ? 'Generating…' : 'Generate images'}
                            </Button>
                            )
                          )
                        )}
                        {step.id === 'open' && step.current && readyForScenes && (
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => navigate(`/narration/${projectId}/scenes`)}
                          >
                            <Film className="w-4 h-4 mr-1" />
                            Open scenes
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                  {currentStep?.id === 'open' && readyForScenes && (
                    <p className="text-xs text-slate-600">
                      All scenes have images. Open scenes to review, edit, and export.
                    </p>
                  )}
                </div>
              )}

              {showSceneViews && (
                <ScriptBasedPipelineSceneViews
                  segments={segments}
                  job={job}
                  imagesInProgress={imagesInProgress}
                  onRetryImage={regenerateSegmentImage}
                  imageRetryBusy={imageRetryBusy}
                />
              )}
            {isStuckQueued && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="mb-2">
                  Pipeline is still queued. Restart the Celery worker, or retry here to run in-process.
                </p>
                <Button type="button" size="sm" variant="outline" onClick={retryPipeline}>
                  Retry pipeline
                </Button>
              </div>
            )}
            {showImageResume && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="mb-2">
                  {missingImageSegments.length} scene(s) still need images
                  {missingImageSegments.length <= 6
                    ? ` (#${missingImageSegments.map((s) => s.segment_number).join(', #')})`
                    : ''}
                  . Image generation stalled
                  {isImageStuck || isFailed
                    ? ` (no progress for ${Math.round(imageStuckTimeoutSec / 60)}+ minutes)`
                    : ''}
                  — click Resume to continue.
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={resumeBusy || Boolean(phaseLoading)}
                  onClick={resumeImages}
                >
                  {resumeBusy ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : null}
                  Resume image generation
                </Button>
              </div>
            )}
            {showAlignResume && !job.error_message && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="mb-2">
                  {missingAlignSegments.length} scene(s) still need WhisperX timing
                  {missingAlignSegments.length <= 6
                    ? ` (#${missingAlignSegments.map((s) => s.segment_number).join(', #')})`
                    : ''}
                  {failedAlignSegments.length > 0
                    ? ` · ${failedAlignSegments.length} failed`
                    : isAlignStuck
                      ? ` · no progress for ${Math.round(alignStuckTimeoutSec / 60)}+ minutes`
                      : ''}
                  .
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={resumeBusy || Boolean(phaseLoading)}
                    onClick={resumeAlign}
                  >
                    {resumeBusy ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : null}
                    Resume WhisperX alignment
                  </Button>
                  {failedAlignSegments.length > 0 && (
                    <Button
                      type="button"
                      size="sm"
                      disabled={resumeBusy || Boolean(phaseLoading)}
                      onClick={skipFailedAlign}
                    >
                      Continue without WhisperX for failed scenes
                    </Button>
                  )}
                </div>
              </div>
            )}
            {showAudioResume && !showAlignResume && !showImageResume && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="mb-2">
                  {missingAudioSegments.length} scene(s) still need audio
                  {missingAudioSegments.length <= 6
                    ? ` (#${missingAudioSegments.map((s) => s.segment_number).join(', #')})`
                    : ''}
                  {isTtsStuck
                    ? ` · no progress for ${Math.round(ttsStuckTimeoutSec / 60)}+ minutes`
                    : ''}
                  . Retry the pipeline from Task history or use Retry pipeline below.
                </p>
                <Button type="button" size="sm" variant="outline" onClick={retryPipeline}>
                  Retry pipeline
                </Button>
              </div>
            )}
            {isStuckAlign && !showAlignResume && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="mb-2">
                  WhisperX alignment has not started. Restart the Celery worker if needed, then resume
                  alignment without re-running TTS.
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={resumeBusy}
                  onClick={resumeAlign}
                >
                  Resume WhisperX alignment
                </Button>
              </div>
            )}

            {trace.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Step log</h3>
                <ul className="space-y-2 text-sm">
                  {trace.map((row, i) => (
                    <li key={`trace-${i}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-bold uppercase text-violet-600">
                          {SCRIPT_BASED_PHASE_LABELS[row.phase] || row.phase}
                        </span>
                        <span className="text-slate-600">{row.message}</span>
                      </div>
                      {row.output && (
                        <pre className="text-[11px] text-slate-500 whitespace-pre-wrap overflow-x-auto mt-1">
                          {JSON.stringify(row.output, null, 2)}
                        </pre>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {segments.length > 0 && !showSceneViews && (
              <div className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Segments</h3>
                <ul className="space-y-2 max-h-48 overflow-y-auto">
                  {segments.map((seg) => (
                    <li key={seg.id} className="text-xs rounded-lg border p-2 bg-white">
                      <div className="font-medium text-slate-700">
                        #{seg.segment_number}
                        {seg.statement_start_time != null && seg.statement_end_time != null
                          ? ` · stmt ${Number(seg.statement_start_time).toFixed(2)}–${Number(seg.statement_end_time).toFixed(2)}s`
                          : seg.timeline_start != null && seg.timeline_end != null
                            ? ` · ${seg.timeline_start}s – ${seg.timeline_end}s`
                            : ''}
                      </div>
                      {seg.audio_url && (
                        <audio controls className="w-full h-8 mt-1" preload="none">
                          <source src={mediaUrlForPlayback(seg.audio_url)} type="audio/mpeg" />
                        </audio>
                      )}
                      {seg.audio_error ? (
                        <p className="text-[10px] mt-1 text-red-600">
                          Audio: failed
                          <span className="block mt-0.5">{seg.audio_error}</span>
                        </p>
                      ) : seg.audio_status === 'failed' ? (
                        <p className="text-[10px] mt-1 text-red-600">Audio: failed</p>
                      ) : null}
                      {seg.image_url && (
                        <img
                          src={mediaUrlForPlayback(seg.image_url)}
                          alt={`Scene ${seg.segment_number}`}
                          className="w-full max-h-24 object-cover rounded mt-1"
                        />
                      )}
                      {seg.align_status && (
                        <p className={`text-[10px] mt-1 ${segmentHasAlignTiming(seg) ? 'text-slate-500' : 'text-amber-700'}`}>
                          Align: {segmentHasAlignTiming(seg) ? 'completed' : seg.align_status || 'pending'}
                          {seg.alignment_source === 'audio_duration' ? ' · audio duration' : ''}
                          {segmentHasWhisperxAlign(seg) && seg.word_timestamps?.length
                            ? ` · ${seg.word_timestamps.length} words`
                            : !segmentHasAlignTiming(seg) && seg.audio_url
                              ? ' · WhisperX needed'
                              : ''}
                          {seg.align_error ? (
                            <span className="block text-red-600 mt-0.5">{seg.align_error}</span>
                          ) : null}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {projectId && (
              <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
                {readyForScenes ? (
                  <Button size="sm" onClick={() => navigate(`/narration/${projectId}/scenes`)}>
                    <Film className="w-4 h-4 mr-1" />
                    Open scenes
                  </Button>
                ) : (
                  <p className="text-xs text-slate-500 self-center">
                    Complete the steps above — Open scenes unlocks after images are generated.
                  </p>
                )}
                {showImageResume ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={resumeBusy || Boolean(phaseLoading)}
                    onClick={resumeImages}
                  >
                    {resumeBusy ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : null}
                    Resume image generation
                  </Button>
                ) : null}
                <Button size="sm" variant="outline" onClick={() => onOpenChange(false)}>
                  Close
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center py-12 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Loading pipeline…
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
